// src/services/AuthService.ts
import { map, Observable, tap } from "rxjs";
import HttpClient, { setToken, setStoredRefresh } from "@/api/HttpClient";
import { APIHosts, ApiReduxHelpers } from "@/services/reduxHelpers";
import { saveUserProfileFromToken, clearUserProfile } from "@/utils/jwtHelper";
import { UploadFileService } from "@/features/upload/services/UploadFileService";

export const getLoginFormBody = (input: {
  username: string;
  password: string;
  captcha?: string;
  captchaId?: string;
  remember?: boolean;
}) => {
  const oAuthConfig = UploadFileService.config?.oAuth ?? {
    clientId: "",
    clientSecret: "",
    scope: "",
  };

  const form: Record<string, string> = {
    grant_type: "password",
    client_id: oAuthConfig.clientId,
    scope: oAuthConfig.scope,
    username: input.username,
    password: input.password,
    captcha: input.captcha || "",
    captchaId: input.captchaId || "",
  };

  if (oAuthConfig.clientSecret) {
    form.client_secret = oAuthConfig.clientSecret;
  }
  if (input.captcha) form["captcha"] = input.captcha;
  if (input.captchaId) form["captchaId"] = input.captchaId;
  if (typeof input.remember !== "undefined")
    form["remember"] = String(input.remember);

  return Object.entries(form)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
};

export interface LoginRequest {
  username: string;
  password: string;
  remember?: boolean;
  captcha?: string;
  captchaId?: string;
}
export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
}

export interface OrgInfo {
  guid: string;
  invitationStatus: string;
  companyName: string;
  billingAddress: string | null;
  billingAddress2: string | null;
  billingCity: string | null;
  billingState: string | null;
  billingZip: string | null;
  crmId: string | null;
  duns: string | null;
  naics: string | null;
  companyId: number;
}
export interface CurrentClientPreferences {
  clientId?: string;
  settings?: unknown;
  userGuid?: string;
  defaultOrganization?: string;
  [key: string]: unknown;
}

class AuthService extends ApiReduxHelpers {
  ApiHost = APIHosts.Authen;
  APILicense = APIHosts.License;

  login(credentials: LoginRequest): Observable<LoginResponse> {
    const body = getLoginFormBody(credentials);

    return HttpClient.post<any>(
      `${this.getHost(this.ApiHost)}/connect/token`,
      body,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      },
    ).pipe(
      map((res) => ({
        accessToken: res.access_token ?? res.accessToken,
        refreshToken: res.refresh_token ?? res.refreshToken,
      })),
      tap((res) => {
        // persist for reloads
        setStoredRefresh(res.refreshToken);
        setToken(res.accessToken);

        // Decode JWT và lưu user profile vào localStorage
      }),
    );
  }
  getCurrentClientPreferences(body?: {
    clientId?: string;
    settings?: unknown;
    userGuid?: string;
    defaultOrganization?: string;
  }): Observable<CurrentClientPreferences> {
    return HttpClient.post<CurrentClientPreferences>(
      `${this.getHost(this.APILicense)}/license_manager/users/current/preferences`,
      body || {},
      {
        headers: {
          Accept: "application/json",
        },
      },
    );
  }

  getCurrentClient(): Observable<CurrentClientPreferences> {
    return HttpClient.get<CurrentClientPreferences>(
      `${this.getHost(
        this.APILicense,
      )}/license_manager/users/current/preferences/currentClient`,
      {
        headers: {
          Accept: "application/json",
        },
      },
    );
  }

  logout() {
    setToken(null);
    setStoredRefresh(null);
    clearUserProfile(); // Xóa user profile khi logout
    localStorage.clear();
    // (optional) this.dispatch({ type: 'auth/logout' });
  }
  getOrganizations(): Observable<OrgInfo[]> {
    return HttpClient.get<OrgInfo[]>(
      `${this.getHost(
        this.APILicense,
      )}/license_manager/users/current/organizations`,
      {
        headers: {
          Accept: "application/json",
        },
      },
    );
  }
  getCaptchaByEmail(email: string): Observable<any> {
    return HttpClient.get<any>(
      `${this.getHost(this.ApiHost)}/clients/captcha/${email}`,
    );
  }

  changePassword(data: {
    oldPassword: string;
    newPassword: string;
  }): Observable<any> {
    return HttpClient.put<any>(
      `${this.getHost(this.APILicense)}/license_manager/users/current/password`,
      data,
    );
  }
}

export default new AuthService();
